if (!customElements.get('variant-picker')) {
  customElements.define(
    'variant-picker',
    class extends HTMLElement {
      connectedCallback() {
        this.variants = this.readVariants();
        this.addEventListener('change', (event) => {
          const input = event.target;
          if (!(input instanceof HTMLInputElement) || input.type !== 'radio') return;
          const fieldset = input.closest('fieldset');
          fieldset?.querySelectorAll('.noble-origins-variant__option').forEach((label) => {
            label.classList.toggle('is-selected', label.contains(input));
          });
          this.scrollOption(input, 'smooth');
          this.syncAvailableOptions();
          this.syncVariant();
        });
        this.syncAvailableOptions();
        window.requestAnimationFrame(() => {
          this.querySelectorAll('input:checked').forEach((input) => {
            this.scrollOption(input, 'auto');
          });
        });
      }

      scrollOption(input, behavior) {
        const label = input.closest('.noble-origins-variant__option');
        const scroller = input.closest('.noble-origins-variant__options');
        if (!label || !scroller) return;

        const scrollerRect = scroller.getBoundingClientRect();
        const labelRect = label.getBoundingClientRect();
        const padLeft = parseFloat(getComputedStyle(scroller).paddingLeft) || 0;
        const endGap = parseFloat(getComputedStyle(label).marginRight) || 0;
        let delta = 0;

        if (labelRect.left < scrollerRect.left + padLeft) {
          delta = labelRect.left - scrollerRect.left - padLeft;
        } else if (labelRect.right > scrollerRect.right - endGap) {
          delta = labelRect.right - scrollerRect.right + endGap;
        }

        if (!delta) return;

        const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        scroller.scrollBy({ left: delta, behavior: reduced ? 'auto' : behavior });
      }

      syncAvailableOptions() {
        const fieldsets = Array.from(this.querySelectorAll('fieldset'));
        const selected = fieldsets.map((fieldset) => fieldset.querySelector('input:checked')?.value ?? null);

        fieldsets.forEach((fieldset, index) => {
          const possible = new Set(
            this.variants
              .filter((variant) =>
                variant.options.every((option, optionIndex) => {
                  if (optionIndex >= index) return true;
                  return option === selected[optionIndex];
                })
              )
              .map((variant) => variant.options[index])
          );
          const inputs = Array.from(fieldset.querySelectorAll('input[type="radio"]'));

          inputs.forEach((input) => {
            const matches = this.variants.filter((variant) =>
              variant.options.every((option, optionIndex) => {
                if (optionIndex > index) return true;
                if (optionIndex === index) return option === input.value;
                return option === selected[optionIndex];
              })
            );
            const exists = matches.length > 0;
            const purchasable = matches.some((variant) => variant.available);
            const label = input.closest('.noble-origins-variant__option');
            input.disabled = !exists;
            label?.classList.toggle('is-unavailable', !exists);
            label?.classList.toggle('is-sold-out', exists && !purchasable);
          });

          if (inputs.some((input) => input.checked && possible.has(input.value))) return;

          const fallback = this.variants.find((variant) =>
            variant.options.every((option, optionIndex) => {
              if (optionIndex >= index) return true;
              return option === selected[optionIndex];
            })
          );
          const nextValue = fallback?.options[index];
          const nextInput = inputs.find((input) => input.value === nextValue);
          if (!nextInput) return;

          nextInput.disabled = false;
          nextInput.checked = true;
          selected[index] = nextValue;
          fieldset.querySelectorAll('.noble-origins-variant__option').forEach((label) => {
            label.classList.toggle('is-selected', label.contains(nextInput));
          });
          this.scrollOption(nextInput, 'smooth');
        });
      }

      readVariants() {
        try {
          return JSON.parse(this.querySelector('[data-variants]')?.textContent || '[]');
        } catch (error) {
          return [];
        }
      }

      syncVariant() {
        const selected = Array.from(this.querySelectorAll('fieldset')).map((fieldset) => {
          return fieldset.querySelector('input:checked')?.value;
        });
        const variant = this.variants.find((item) => {
          return item.options.every((option, index) => option === selected[index]);
        });
        if (!variant) return;

        this.dataset.variantId = String(variant.id);
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url);
        this.dispatchEvent(
          new CustomEvent('noble-origins:variant-change', {
            bubbles: true,
            detail: { variant },
          })
        );
      }
    }
  );
}
